<template>
  <div class="page">
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>菜单管理</span>
        <el-button type="primary" size="small" @click="openDialog()">新增菜单</el-button>
      </div>
      <el-table
        v-loading="loading"
        :data="treeList"
        border
        stripe
        row-key="id"
        default-expand-all
        :tree-props="{ children: 'children' }"
      >
        <el-table-column prop="menuName" label="菜单名称" min-width="180" show-overflow-tooltip />
        <el-table-column prop="menuCode" label="编码" min-width="130" show-overflow-tooltip />
        <el-table-column prop="path" label="路径" min-width="180" show-overflow-tooltip />
        <el-table-column label="父菜单" min-width="120" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ parentNameOf(row.parentId) }}
          </template>
        </el-table-column>
        <el-table-column label="类型" width="80">
          <template slot-scope="{ row }">
            <el-tag size="small" type="info">{{ labelOf(MENU_TYPE, row.menuType) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="sortOrder" label="排序" width="70" align="center" />
        <el-table-column label="可见" width="70" align="center">
          <template slot-scope="{ row }">{{ row.visible === 1 ? '是' : '否' }}</template>
        </el-table-column>
        <el-table-column label="状态" width="80" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'info'" size="small">
              {{ labelOf(COMMON_STATUS, row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="160" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openDialog(row)">编辑</el-button>
            <el-button type="text" size="small" @click="openDialog(null, row)">新增子项</el-button>
            <el-button type="text" size="small" class="danger" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog :title="dialogTitle" :visible.sync="visible" width="520px" @closed="resetForm">
      <el-form ref="form" :model="form" :rules="rules" label-width="88px">
        <el-form-item label="名称" prop="menuName">
          <el-input v-model="form.menuName" />
        </el-form-item>
        <el-form-item label="编码" prop="menuCode">
          <el-input v-model="form.menuCode" />
        </el-form-item>
        <el-form-item label="父菜单">
          <el-select
            v-model="form.parentId"
            filterable
            placeholder="顶级菜单"
            style="width: 100%"
          >
            <el-option label="顶级菜单" :value="0" />
            <el-option
              v-for="item in parentSelectOptions"
              :key="item.id"
              :label="item.label"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="类型">
          <el-select v-model="form.menuType" style="width: 100%">
            <el-option v-for="opt in menuTypeOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="路径">
          <el-input v-model="form.path" placeholder="/admin/..." />
        </el-form-item>
        <el-form-item label="组件">
          <el-input v-model="form.component" />
        </el-form-item>
        <el-form-item label="图标">
          <el-input v-model="form.icon" placeholder="dashboard / users" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sortOrder" :min="0" />
        </el-form-item>
        <el-form-item label="可见">
          <el-select v-model="form.visible" style="width: 100%">
            <el-option label="是" :value="1" />
            <el-option label="否" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="form.status" style="width: 100%">
            <el-option v-for="opt in statusOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="visible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submit">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getSystemMenus, saveSystemMenu, deleteSystemMenu } from '@/api/admin'
import { COMMON_STATUS, MENU_TYPE, labelOf, optionsOf } from '@/utils/adminConstants'

function buildMenuTree(flatList) {
  if (!flatList || !flatList.length) return []
  const map = {}
  flatList.forEach(item => {
    map[item.id] = { ...item, children: [] }
  })
  const roots = []
  flatList.forEach(item => {
    const node = map[item.id]
    const parentId = item.parentId || 0
    if (!parentId || !map[parentId]) {
      roots.push(node)
    } else {
      map[parentId].children.push(node)
    }
  })
  const sortTree = nodes => {
    nodes.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    nodes.forEach(node => {
      if (node.children.length) {
        sortTree(node.children)
      } else {
        delete node.children
      }
    })
  }
  sortTree(roots)
  return roots
}

function flattenTree(nodes, prefix = '') {
  const result = []
  nodes.forEach(node => {
    result.push({ id: node.id, label: prefix + node.menuName })
    if (node.children && node.children.length) {
      result.push(...flattenTree(node.children, prefix + '　'))
    }
  })
  return result
}

function collectDescendantIds(node, set = new Set()) {
  if (!node) return set
  set.add(node.id)
  ;(node.children || []).forEach(child => collectDescendantIds(child, set))
  return set
}

function findNodeInTree(tree, id) {
  for (const node of tree) {
    if (node.id === id) return node
    if (node.children && node.children.length) {
      const found = findNodeInTree(node.children, id)
      if (found) return found
    }
  }
  return null
}

export default {
  name: 'SystemMenus',
  data() {
    return {
      COMMON_STATUS,
      MENU_TYPE,
      loading: false,
      flatList: [],
      treeList: [],
      nameMap: {},
      visible: false,
      submitting: false,
      dialogTitle: '新增菜单',
      form: this.defaultForm()
    }
  },
  computed: {
    statusOptions() {
      return optionsOf(COMMON_STATUS)
    },
    menuTypeOptions() {
      return optionsOf(MENU_TYPE)
    },
    parentSelectOptions() {
      const excludeIds = new Set()
      if (this.form.id) {
        const current = findNodeInTree(this.treeList, this.form.id)
        collectDescendantIds(current, excludeIds)
        excludeIds.add(this.form.id)
      }
      return flattenTree(this.treeList).filter(item => !excludeIds.has(item.id))
    }
  },
  created() {
    this.loadMenus()
  },
  methods: {
    labelOf,
    defaultForm() {
      return {
        id: null,
        menuName: '',
        menuCode: '',
        path: '',
        component: '',
        icon: '',
        parentId: 0,
        menuType: 2,
        sortOrder: 0,
        visible: 1,
        status: 1
      }
    },
    parentNameOf(parentId) {
      if (!parentId || parentId === 0) return '顶级菜单'
      return this.nameMap[parentId] || '-'
    },
    async loadMenus() {
      this.loading = true
      try {
        const list = await getSystemMenus() || []
        this.flatList = list
        this.nameMap = list.reduce((acc, item) => {
          acc[item.id] = item.menuName
          return acc
        }, {})
        this.treeList = buildMenuTree(list)
      } catch (e) {
      } finally {
        this.loading = false
      }
    },
    openDialog(row, parentRow) {
      if (row) {
        this.dialogTitle = '编辑菜单'
        const { children, ...rest } = row
        this.form = { ...rest, parentId: row.parentId || 0 }
      } else {
        this.dialogTitle = parentRow ? `新增子菜单（${parentRow.menuName}）` : '新增菜单'
        this.form = {
          ...this.defaultForm(),
          parentId: parentRow ? parentRow.id : 0,
          menuType: parentRow ? 2 : 1
        }
      }
      this.visible = true
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate()
      })
    },
    resetForm() {
      this.form = this.defaultForm()
    },
    submit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          await saveSystemMenu(this.form)
          this.$message.success('保存成功')
          this.visible = false
          this.loadMenus()
        } catch (e) {
        } finally {
          this.submitting = false
        }
      })
    },
    handleDelete(row) {
      const hasChildren = row.children && row.children.length
      const msg = hasChildren
        ? '该菜单下存在子菜单，确定删除？（请先处理子菜单）'
        : '确定删除该菜单？'
      this.$confirm(msg, '提示', { type: 'warning' }).then(async () => {
        try {
          await deleteSystemMenu(row.id)
          this.$message.success('已删除')
          this.loadMenus()
        } catch (e) {
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.danger {
  color: #f56c6c;
}
</style>
